/**
 * Archive gallery lightbox.
 * ScrollSmoother transforms #smooth-content, so fixed descendants are viewport-trapped
 * and stack under the header (outside the wrapper). Move overlay to document.body when open.
 */
class ArchiveLightbox {
  constructor(root) {
    this.root = root;
    this.lightbox = this.root.querySelector('[data-lightbox]');
    if (!this.lightbox) return;

    this.slides = this.lightbox.querySelectorAll('[data-lightbox-slide]');
    this.currentEl = this.lightbox.querySelector('[data-lightbox-current]');
    this.totalEl = this.lightbox.querySelector('[data-lightbox-total]');
    this.currentIndex = 0;
    this.total = this.slides.length;

    this.restoreParent = null;
    this.restoreNextSibling = null;
    this.isOpen = false;

    this.bindTriggers();
    this.bindControls();
    this.bindKeyboard();
  }

  getSmoothWrapper() {
    return document.getElementById('smooth-wrapper');
  }

  getSmoother() {
    return typeof window.scrollSmoother !== 'undefined' ? window.scrollSmoother : null;
  }

  bindTriggers() {
    this.root.querySelectorAll('[data-lightbox-trigger]').forEach((trigger, index) => {
      trigger.addEventListener('click', () => {
        this.open(index);
      });
    });
  }

  bindControls() {
    const prevBtn = this.lightbox.querySelector('[data-lightbox-prev]');
    const nextBtn = this.lightbox.querySelector('[data-lightbox-next]');

    if (prevBtn) {
      prevBtn.addEventListener('click', e => {
        e.stopPropagation();
        this.prev();
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', e => {
        e.stopPropagation();
        this.next();
      });
    }

    const stage = this.lightbox.querySelector('[data-lightbox-close]');
    if (stage) {
      stage.addEventListener('click', e => {
        if (
          e.target === stage ||
          e.target.classList.contains('archive-lightbox__image') ||
          e.target.closest('[data-lightbox-slide]')
        ) {
          this.close();
        }
      });
    }

    this.lightbox.querySelector('.archive-lightbox__header')?.addEventListener('click', e => {
      e.stopPropagation();
    });
  }

  bindKeyboard() {
    document.addEventListener('keydown', e => {
      if (!this.isOpen) return;

      switch (e.key) {
        case 'Escape':
          this.close();
          break;
        case 'ArrowLeft':
          this.prev();
          break;
        case 'ArrowRight':
          this.next();
          break;
      }
    });
  }

  mountToBody() {
    if (this.lightbox.parentElement === document.body) return;

    this.restoreParent = this.lightbox.parentNode;
    this.restoreNextSibling = this.lightbox.nextSibling;
    document.body.appendChild(this.lightbox);
  }

  restoreToSection() {
    if (!this.restoreParent || this.lightbox.parentElement !== document.body) return;

    if (this.restoreNextSibling && this.restoreNextSibling.parentNode === this.restoreParent) {
      this.restoreParent.insertBefore(this.lightbox, this.restoreNextSibling);
    } else {
      this.restoreParent.appendChild(this.lightbox);
    }
  }

  lockScroll() {
    document.body.classList.add('archive-lightbox--scroll-locked');
    document.documentElement.classList.add('archive-lightbox--scroll-locked');

    const wrapper = this.getSmoothWrapper();
    if (wrapper) {
      wrapper.dataset.lightboxPrevOverflow = wrapper.style.overflow || '';
      wrapper.style.overflow = 'hidden';
    }

    const smoother = this.getSmoother();
    if (smoother && typeof smoother.paused === 'function') {
      smoother.paused(true);
    }
  }

  unlockScroll() {
    document.body.classList.remove('archive-lightbox--scroll-locked');
    document.documentElement.classList.remove('archive-lightbox--scroll-locked');

    const wrapper = this.getSmoothWrapper();
    if (wrapper) {
      const prev = wrapper.dataset.lightboxPrevOverflow;
      if (prev !== undefined) {
        wrapper.style.overflow = prev;
        delete wrapper.dataset.lightboxPrevOverflow;
      }
    }

    const smoother = this.getSmoother();
    if (smoother && typeof smoother.paused === 'function') {
      smoother.paused(false);
    }
  }

  open(index) {
    this.currentIndex = index;
    this.updateSlide();
    this.mountToBody();
    this.lockScroll();
    this.lightbox.setAttribute('aria-hidden', 'false');
    this.isOpen = true;
  }

  close() {
    this.lightbox.setAttribute('aria-hidden', 'true');
    this.isOpen = false;
    this.unlockScroll();
    this.restoreToSection();
  }

  prev() {
    this.currentIndex = (this.currentIndex - 1 + this.total) % this.total;
    this.updateSlide();
  }

  next() {
    this.currentIndex = (this.currentIndex + 1) % this.total;
    this.updateSlide();
  }

  updateSlide() {
    this.slides.forEach(slide => slide.classList.remove('archive-lightbox__slide--active'));
    if (this.slides[this.currentIndex]) {
      this.slides[this.currentIndex].classList.add('archive-lightbox__slide--active');
    }

    const display = String(this.currentIndex + 1).padStart(2, '0');
    const totalDisplay = String(this.total).padStart(2, '0');
    if (this.currentEl) this.currentEl.textContent = display;
    if (this.totalEl) this.totalEl.textContent = totalDisplay;
  }
}

function initArchiveLightboxes(container = document) {
  const galleries = [];

  if (container.matches?.('[data-archive-gallery]')) {
    galleries.push(container);
  }

  galleries.push(...container.querySelectorAll('[data-archive-gallery]'));

  galleries.forEach(gallery => {
    if (gallery.dataset.archiveLightboxInitialized === 'true') return;

    gallery.dataset.archiveLightboxInitialized = 'true';
    new ArchiveLightbox(gallery);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initArchiveLightboxes());
} else {
  initArchiveLightboxes();
}

document.addEventListener('shopify:section:load', event => {
  initArchiveLightboxes(event.target);
});
